<template>
    <!-- Stop the inner form's submit event from bubbling to the modal's outer <form>: the fields are
         validated imperatively from the modal's submit handler, not by pressing Enter inside them. -->
    <div class="dynamic-request-form" @submit.prevent.stop>
        <FormulateForm ref="form" v-model="model" @submit="noop">
            <FormulateInput
                v-for="field in fields"
                :key="field.id"
                :type="typeFor(field)"
                :name="field.key"
                :label="field.label"
                :validation="validationFor(field)"
                :validation-name="field.label"
                :options="optionsFor(field)"
                :placeholder="placeholderFor(field)"
            ></FormulateInput>
        </FormulateForm>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import RequestServiceFormFieldType from '@/js/enums/RequestServiceFormFieldType';

const TYPE = RequestServiceFormFieldType;

@Component
class DynamicRequestForm extends Vue {
    @Prop({ type: Array, required: true }) fields;
    // Optional values to pre-fill from (e.g. restored from the URL), as [{ fieldId, value }]. Matched
    // to fields by fieldId, so entries for fields this service doesn't have are ignored.
    @Prop({ type: Array, default: null }) initialValues;

    model = {};

    typeFor(field) {
        switch (field.type) {
            case TYPE.NUMBER:
                return 'number';
            case TYPE.FILE:
                return 'vfile';
            case TYPE.SELECT:
                return 'vselect';
            case TYPE.MULTISELECT:
                return 'vmultiselect';
            case TYPE.TEXT:
                return 'textarea';
            // LINK is a single-line text input, validated by the custom `httpurl` rule.
            default:
                return 'text';
        }
    }

    validationFor(field) {
        const rules = [];

        if (field.required) {
            rules.push('required');
        }

        switch (field.type) {
            case TYPE.NUMBER:
                rules.push('number');
                break;
            case TYPE.LINK:
                rules.push('httpurl');
                break;
            case TYPE.TEXT:
                rules.push('max:5000,length');
                break;
        }

        return rules.join('|');
    }

    optionsFor(field) {
        if (field.type === TYPE.SELECT || field.type === TYPE.MULTISELECT) {
            // The backend resolves each select's effective options — hand-entered or from a linked
            // dictionary — into `field.options`, so this one source covers both. Fall back to the raw
            // settings for a payload that predates that resolution.
            if (Array.isArray(field.options)) {
                return field.options;
            }

            return (field.settings && field.settings.options) || [];
        }

        return undefined;
    }

    placeholderFor(field) {
        if (field.type === TYPE.LINK) {
            return 'https://…';
        }

        return '';
    }

    noop() {}

    /**
     * Runs Formulate's validation across every field and surfaces the messages. The create-request
     * modal awaits this before it will submit, so a service form with unfilled required fields cannot
     * be posted as an empty request.
     *
     * @returns {Promise<boolean>} true when the form is valid.
     */
    async validate() {
        const form = this.$refs.form;
        const hasErrors = await form.hasValidationErrors();

        if (hasErrors) {
            form.showErrors();
        }

        return !hasErrors;
    }

    emitValues() {
        this.$emit(
            'input',
            this.fields.map((field) => {
                const value = this.model[field.key];

                return { fieldId: field.id, value: value === undefined ? null : value };
            }),
        );
    }

    @Watch('model', { deep: true })
    onModelChange() {
        this.emitValues();
    }

    /**
     * Immediate, so the parent's copy of the values matches this form from the very start; re-run on a
     * field-set change so switching to another service cannot leave the parent holding stale values or
     * Formulate holding the previous service's entries.
     */
    @Watch('fields', { immediate: true })
    onFieldsChange() {
        const model = {};
        const restored = this.restoredValuesByFieldId();

        this.fields.forEach((field) => {
            const hasRestored = Object.prototype.hasOwnProperty.call(restored, field.id);
            const value = hasRestored ? restored[field.id] : undefined;

            if (field.type === TYPE.FILE || field.type === TYPE.MULTISELECT) {
                model[field.key] = Array.isArray(value) ? value : [];
            } else if (field.type === TYPE.SELECT) {
                model[field.key] = value === undefined || value === null ? null : value;
            } else {
                model[field.key] = value === undefined || value === null ? '' : value;
            }
        });

        this.model = model;
        this.emitValues();
    }

    /**
     * Indexes initialValues by fieldId for the seeding above. FILE fields never appear here (the modal
     * strips them from the URL), so they always start empty.
     *
     * @returns {Object} fieldId -> value
     */
    restoredValuesByFieldId() {
        const map = {};

        (this.initialValues || []).forEach((entry) => {
            if (entry && entry.fieldId !== null && entry.fieldId !== undefined) {
                map[entry.fieldId] = entry.value;
            }
        });

        return map;
    }
}

export default DynamicRequestForm;
</script>
<style lang="scss">
.dynamic-request-form .formulate-input-element textarea {
    min-height: 8rem;
}

// The form sits between the category selector and the observers field. Every field — including the
// last one — keeps its bottom margin so the gap before the observers field matches the spacing
// between the form's own fields (previously the last field's margin was zeroed, butting them together).
</style>
